import { useEffect, useState } from 'react'
import { SvgIcon, SvgIconType } from '@integration-app/react'
import clsx from 'clsx'
import { TbExternalLink } from 'react-icons/tb'
import { NavLink, useNavigate } from 'react-router-dom'

import { useDocMenu } from 'routes/Docs/components/DocsMenu/useDocMenu'
import {
  MenuItemType,
  isMenuItemLinkType,
  MenuItemLinkType,
  MenuItemHeaderType,
} from 'routes/Docs/types'
import { Box } from 'styled-system/jsx'
import { visuallyHidden } from 'styled-system/patterns'
import { Badge } from 'ui-kit/badge'
import { Icon } from 'ui-kit/icon'

import classes from './Menu.module.css'

export function FullDocsMenu() {
  const docMenu = useDocMenu()

  return <Menu className={classes.menu} items={docMenu} />
}

export function SecondLevelDocsMenu() {
  const docMenu = useDocMenu()
  const secondLevelMenu = docMenu.find(
    (item) => item?.isActive || itemHasActiveChild(item),
  )?.links

  return <Menu className={classes.menu} items={secondLevelMenu} />
}

function Menu({
  items,
  className,
}: {
  items?: MenuItemType[]
  className?: string
}) {
  if (!items?.length) {
    return null
  }
  return (
    <ul className={className}>
      {items.map((item, i) => (
        <MenuItem item={item} key={i} />
      ))}
    </ul>
  )
}

function MenuItem({ item }: { item: MenuItemType }) {
  const [isExpand, setIsExpand] = useState(item.isExpand ?? false)

  useEffect(() => {
    setIsExpand(item.isExpand ?? false)
  }, [item.isExpand])

  const isExpandable = (item.links?.length || 0) > 0

  const toggleExpand = () => {
    if (!isExpandable) return
    setIsExpand(!isExpand)
  }

  return (
    <li>
      <span className={classes.menuItem}>
        {isMenuItemLinkType(item) ? (
          <MenuItemLink item={item} />
        ) : (
          <MenuItemHeader onToggleClick={toggleExpand} item={item} />
        )}

        {isExpandable && <Chevron isExpand={isExpand} onClick={toggleExpand} />}

        {item.isBeta && (
          <Box
            position='absolute'
            right='-1'
            top='50%'
            transform='translate(0, -50%)'
          >
            <Badge size='sm' hideBelow='lg'>
              beta
            </Badge>
            <Badge size='sm' hideFrom='lg'>
              <span aria-hidden='true'>b</span>
              <span className={visuallyHidden()}>beta</span>
            </Badge>
          </Box>
        )}
      </span>
      {isExpandable && isExpand && <Menu items={item.links} />}
    </li>
  )
}

function MenuItemLink({ item }: { item: MenuItemLinkType }) {
  if (item.isExternalLink) {
    return (
      <a
        className={clsx(
          classes.menuItem_title,
          classes.menuItem_title__external,
        )}
        href={item.to}
        rel='noreferrer'
      >
        {item.title}
        <Icon size='sm'>
          <TbExternalLink />
        </Icon>
      </a>
    )
  }
  return (
    <NavLink
      className={clsx(
        classes.menuItem_title,
        item.isActive && classes.menuItem_title__active,
        item.links?.length && classes.menuItem_title__expandable,
      )}
      to={item.to}
    >
      {item.title}
    </NavLink>
  )
}

function MenuItemHeader({
  item,
  onToggleClick,
}: {
  item: MenuItemHeaderType
  onToggleClick: () => void
}) {
  const navigate = useNavigate()
  const isExpandable = item.links?.length

  function handleClick() {
    if (!isExpandable) {
      return
    }

    // If has active child, toggle expand section
    if (itemHasActiveChild(item)) {
      return onToggleClick()
    }

    // If has no active child, navigate to first child
    const firstChildLink = item?.links?.find((item) =>
      isMenuItemLinkType(item),
    ) as MenuItemLinkType
    if (!!firstChildLink) {
      void navigate(firstChildLink.to)
    }
  }

  return (
    <span
      className={clsx(
        classes.menuItem_title,
        isExpandable && classes.menuItem_title__expandable,
      )}
      onClick={handleClick}
    >
      {item.title}
    </span>
  )
}

function Chevron({
  isExpand,
  onClick,
}: {
  isExpand?: boolean
  onClick: () => void
}) {
  return (
    <button onClick={onClick} className={classes.chevronButton}>
      <SvgIcon
        type={isExpand ? SvgIconType.ChevronDown : SvgIconType.ChevronRight}
        className={classes.chevronButton_icon}
      />
    </button>
  )
}

function itemHasActiveChild(item: MenuItemType) {
  return item.links?.some((item) => item.isActive || itemHasActiveChild(item))
}
